/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import { types } from 'mobx-state-tree';

// Distinct from ScEnvView's 'swb-workspace-view-compact' so the catalogue page and the
// workspaces page keep independent preferences (Requirements 1.6, 1.10).
const COMPACT_MODE_STORAGE_KEY = 'swb-catalogue-view-compact';

// Storage is resolved through a seam so tests can inject a fake (the jest environment is
// 'node', where localStorage does not exist) and so a throwing or absent storage degrades
// quietly to Full view (Requirements 1.8, 1.9).
function getStorage() {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch (_) {
    return undefined;
  }
}

// Any value other than 'true' resolves to Full view. Reading never writes a default back.
function readSavedCompactMode(storage = getStorage()) {
  try {
    return (storage && storage.getItem(COMPACT_MODE_STORAGE_KEY)) === 'true';
  } catch (_) {
    return false;
  }
}

function saveCompactMode(value, storage = getStorage()) {
  try {
    if (storage) storage.setItem(COMPACT_MODE_STORAGE_KEY, String(value));
  } catch (_) {
    /* noop — the preference is a convenience, never a blocker */
  }
}

const CatalogueView = types
  .model('CatalogueView', {
    compactMode: types.optional(types.boolean, false),
  })
  .actions(self => ({
    setCompactMode(value) {
      self.compactMode = !!value;
      saveCompactMode(self.compactMode);
    },
    toggleCompactMode() {
      self.setCompactMode(!self.compactMode);
    },
  }));

function registerContextItems(appContext) {
  appContext.CatalogueView = CatalogueView.create({ compactMode: readSavedCompactMode() }, appContext);
}

export { CatalogueView, registerContextItems, readSavedCompactMode, saveCompactMode, COMPACT_MODE_STORAGE_KEY };
